"use client";

import { useEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
} from "framer-motion";

// Same "‹ DRAG ›" pill and follow spring as the home strategy slider.
const SPRING = { damping: 25, stiffness: 200, mass: 0.5 };
const INITIAL = { opacity: 0, scale: 0.7 } as const;
const ANIMATE = { opacity: 1, scale: 1 } as const;
const TRANSITION = { duration: 0.2 };

function Chevron({ flip }: { flip?: boolean }) {
  return (
    <svg
      className={`h-[2rem] w-[2rem] flex-shrink-0${flip ? " rotate-180" : ""}`}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="none"
      viewBox="0 0 16 16"
      aria-hidden="true">
      <path
        fill="#fff"
        d="m5.003 8 5 5 .7-.7-4.3-4.3 4.3-4.3-.7-.7z"
      />
    </svg>
  );
}

/** Follows the mouse while it is over `targetRef`. Hide the native cursor there in CSS. */
export default function DragCursor({
  targetRef,
}: {
  targetRef: RefObject<HTMLElement | null>;
}) {
  const [mounted, setMounted] = useState(false);
  const [inside, setInside] = useState(false);
  const insideRef = useRef(false);
  const reduced = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, SPRING);
  const springY = useSpring(y, SPRING);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;
    const onMove = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      if (!insideRef.current) {
        x.jump(event.clientX);
        y.jump(event.clientY);
        springX.jump(event.clientX);
        springY.jump(event.clientY);
        insideRef.current = true;
        setInside(true);
        return;
      }
      x.set(event.clientX);
      y.set(event.clientY);
    };
    const onLeave = () => {
      insideRef.current = false;
      setInside(false);
    };
    target.addEventListener("pointerenter", onMove);
    target.addEventListener("pointermove", onMove);
    target.addEventListener("pointerleave", onLeave);
    return () => {
      target.removeEventListener("pointerenter", onMove);
      target.removeEventListener("pointermove", onMove);
      target.removeEventListener("pointerleave", onLeave);
    };
  }, [targetRef, x, y, springX, springY]);

  if (!mounted) return null;
  return createPortal(
    <AnimatePresence>
      {inside && (
        <motion.div
          aria-hidden="true"
          className="pointer-events-none fixed top-0 left-0 z-[60] flex w-fit items-center justify-center gap-[0.125rem] select-none"
          style={{
            x: reduced ? x : springX,
            y: reduced ? y : springY,
            translateX: "-50%",
            translateY: "-50%",
          }}
          initial={INITIAL}
          animate={ANIMATE}
          exit={INITIAL}
          transition={TRANSITION}>
          <div className="flex h-[4rem] w-[4rem] items-center justify-center border border-white/25 bg-black text-white">
            <Chevron />
          </div>
          <div className="flex h-[4rem] items-center border-t border-b border-white/25 bg-black px-[1rem] font-spacemono text-[2.25rem] text-white uppercase">
            Drag
          </div>
          <div className="flex h-[4rem] w-[4rem] items-center justify-center border border-white/25 bg-black text-white">
            <Chevron flip />
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
